import { useCallback, useEffect, useRef } from 'react';

import { buildToggleChange, buildTransition } from '@zep/logger/tracking';

import {
  type QuizBankFilterAxis,
  type QuizBankPlace,
  type QuizBankQuerySource,
} from '../tracking/events.quiz-bank';
import {
  type QuizBankFallbackState,
  type QuizBankFilterState,
  type QuizBankProblem,
  type QuizBankQuizType,
  type QuizBankSortKey,
} from '../types';
import { toQuizBankInitialGradeId } from '../utils';

import { useQuizBankTrack } from './useQuizBankTrack';

/** 발화 시점의 화면 상태. */
interface QuizBankTrackingSnapshot {
  place: QuizBankPlace;
  categoryTreeId: string;
  filter: QuizBankFilterState;
  status: 'loading' | 'success' | 'error';
  /** 결과·0건·실패를 적용된 필터 조합마다 한 번씩만 세기 위한 키 */
  resultStateKey: string;
  resultCount: number;
  loadedProblemCount: number;
  /** 키워드가 단원명에 걸린 단원 */
  unitHitTermsById?: ReadonlyMap<string, readonly string[]>;
  /** 코드 검색이 걸린 성취기준 코드 */
  matchedStandardCodes?: ReadonlySet<string>;
  selectedProblems: QuizBankProblem[];
  addedProblemCount: number;
  maxSelectableCount?: number;
  fallback?: QuizBankFallbackState;
}

const NO_FALLBACK: QuizBankFallbackState = {
  count: 0,
  pending: false,
  applied: null,
};

/** 여러 payload 가 함께 쓰는 파생값. 슬롯 상한을 모르면 -1 — 카탈로그의 "알 수 없음" 값이다. */
const factsOf = (snapshot: QuizBankTrackingSnapshot) => ({
  place: snapshot.place,
  gradeId: snapshot.filter.gradeId ?? '',
  hasQuery: snapshot.filter.query.trim().length > 0,
  unitFilterCount: snapshot.filter.unitIds.length,
  typeFilterCount: snapshot.filter.types.length,
  remainingSlotCount: snapshot.maxSelectableCount ?? -1,
  fallback: snapshot.fallback ?? NO_FALLBACK,
});

/** 열림·이탈을 한 줄로 잇는 공통 키. */
const surfaceOf = (snapshot: QuizBankTrackingSnapshot) => ({
  place: snapshot.place,
  category_tree_id: snapshot.categoryTreeId,
  initial_grade_id: toQuizBankInitialGradeId(snapshot.categoryTreeId) ?? '',
  remaining_slot_count: snapshot.maxSelectableCount ?? -1,
  added_problem_count: snapshot.addedProblemCount,
});

/**
 * 문제 은행의 분석 이벤트를 한곳에서 소유한다. 화면은 상태만 넘기고 비즈니스 로직에 발화를
 * 섞지 않는다 — 상태로 정해지는 이벤트(열림·이탈·결과 도착·자동 완화)는 여기 effect 가 직접
 * 발화하고, 사용자 조작은 이름 붙은 콜백으로 돌려준다.
 *
 * 콜백은 전부 고정 아이덴티티다. 매 렌더 바뀌는 값은 ref 스냅샷으로만 읽는다.
 */
export const useQuizBankTracking = (snapshot: QuizBankTrackingSnapshot) => {
  const track = useQuizBankTrack();
  const state = useRef(snapshot);
  state.current = snapshot;

  // 담고 나간 경우는 이탈이 아니다.
  const hasImported = useRef(false);

  useEffect(() => {
    track('quiz_bank_modal_open', surfaceOf(state.current));

    return () => {
      if (hasImported.current) return;
      const current = state.current;
      track('quiz_bank_modal_close', {
        ...surfaceOf(current),
        selected_count: current.selectedProblems.length,
        has_selected_question: current.selectedProblems.length > 0,
        result_count: current.resultCount,
      });
    };
  }, [track]);

  // 첫 페이지 응답 확정 — viewport 노출이 아니라 응답 도착이라는 논리적 emission 이다.
  // 다음 페이지 도착·정렬 재조회는 같은 결과 집합이라 키가 안 바뀌어 다시 세지 않는다.
  const trackedResultKey = useRef<string | null>(null);
  const { status, resultStateKey } = snapshot;
  useEffect(() => {
    if (status !== 'success' || trackedResultKey.current === resultStateKey) {
      return;
    }
    trackedResultKey.current = resultStateKey;
    const current = state.current;
    const facts = factsOf(current);
    track('quiz_bank_search_result_view', {
      place: facts.place,
      has_query: facts.hasQuery,
      query: current.filter.query,
      result_count: current.resultCount,
      unit_name_hit_count: current.unitHitTermsById?.size ?? 0,
      standard_code_hit_count: current.matchedStandardCodes?.size ?? 0,
      grade_id: facts.gradeId,
      unit_filter_count: facts.unitFilterCount,
      type_filter_count: facts.typeFilterCount,
      fallback_count: facts.fallback.count,
    });
  }, [track, status, resultStateKey]);

  // 시스템이 필터를 바꾸는 유일한 지점. 적용 때마다 새 객체라 발화도 한 번이다.
  const { applied } = factsOf(snapshot).fallback;
  useEffect(() => {
    if (!applied) return;
    track('quiz_bank_search_fallback_change', {
      place: state.current.place,
      query: applied.query,
      fallback_axis: applied.axis,
      fallback_index: applied.index,
      ...buildTransition(
        'grade_id',
        applied.beforeGradeId,
        applied.afterGradeId,
      ),
      cleared_unit_count: applied.clearedUnitCount,
      cleared_type_count: applied.clearedTypeCount,
    });
  }, [track, applied]);

  /** 화면이 열릴 때마다 새로 세므로 곧 이 세션의 탐색 깊이다. */
  const nextPageIndex = useRef(0);

  return {
    nextPageStarted: useCallback(() => {
      const current = state.current;
      nextPageIndex.current += 1;
      track('quiz_bank_list_next_page_start', {
        place: current.place,
        page_index: nextPageIndex.current,
        loaded_problem_count: current.loadedProblemCount,
        result_count: current.resultCount,
        grade_id: factsOf(current).gradeId,
      });
    }, [track]),

    gradeChanged: useCallback(
      (nextGradeId: string) => {
        const facts = factsOf(state.current);
        track('quiz_bank_filter_grade_change', {
          place: facts.place,
          ...buildTransition('grade_id', facts.gradeId, nextGradeId),
          cleared_unit_count: facts.unitFilterCount,
        });
      },
      [track],
    ),

    filterToggled: useCallback(
      (next: {
        axis: QuizBankFilterAxis;
        value: string;
        checked: boolean;
        unitFilterCount: number;
        typeFilterCount: number;
      }) =>
        track('quiz_bank_filter_option_toggle', {
          place: state.current.place,
          ...buildToggleChange(next.checked),
          filter_axis: next.axis,
          filter_value: next.value,
          unit_filter_count: next.unitFilterCount,
          type_filter_count: next.typeFilterCount,
        }),
      [track],
    ),

    filterReset: useCallback(() => {
      const facts = factsOf(state.current);
      track('quiz_bank_filter_reset_click', {
        place: facts.place,
        grade_id: facts.gradeId,
        unit_filter_count: facts.unitFilterCount,
        type_filter_count: facts.typeFilterCount,
        has_query: facts.hasQuery,
      });
    }, [track]),

    searchSubmitted: useCallback(
      (query: string, source: QuizBankQuerySource) =>
        track('quiz_bank_search_submit', {
          place: state.current.place,
          query,
          query_length: query.length,
          query_source: source,
          grade_id: factsOf(state.current).gradeId,
        }),
      [track],
    ),

    searchCleared: useCallback(
      () =>
        track('quiz_bank_search_clear_click', {
          place: state.current.place,
          query_length: state.current.filter.query.length,
        }),
      [track],
    ),

    sortChanged: useCallback(
      (nextSort: QuizBankSortKey) =>
        track('quiz_bank_sort_change', {
          place: state.current.place,
          ...buildTransition('sort', state.current.filter.sort, nextSort),
        }),
      [track],
    ),

    problemToggled: useCallback(
      (problem: QuizBankProblem, willSelect: boolean) => {
        const current = state.current;
        track('quiz_bank_problem_toggle', {
          place: current.place,
          ...buildToggleChange(willSelect),
          problem_id: problem.id,
          quiz_type: problem.type,
          grade_id: problem.gradeId,
          standard_code: problem.standard.code,
          selected_count:
            current.selectedProblems.length + (willSelect ? 1 : -1),
          remaining_slot_count: factsOf(current).remainingSlotCount,
        });
      },
      [track],
    ),

    importSubmitted: useCallback(() => {
      hasImported.current = true;
      const current = state.current;
      const facts = factsOf(current);
      const countType = (type: QuizBankQuizType) =>
        current.selectedProblems.filter(problem => problem.type === type)
          .length;
      track('quiz_bank_problem_import_submit', {
        place: facts.place,
        problem_count: current.selectedProblems.length,
        multiple_count: countType('multiple'),
        subjective_count: countType('subjective'),
        ox_count: countType('ox'),
        grade_id: facts.gradeId,
        unit_filter_count: facts.unitFilterCount,
        type_filter_count: facts.typeFilterCount,
        has_query: facts.hasQuery,
        sort: current.filter.sort,
        fallback_count: facts.fallback.count,
        destination: 'build_form',
      });
    }, [track]),

    /** 완화가 곧 걸릴 0건은 막다른 길이 아니라 세지 않는다. */
    emptyResultViewed: useCallback(() => {
      const current = state.current;
      const facts = factsOf(current);
      if (facts.fallback.pending) return;
      track('quiz_bank_result_empty_view', {
        place: facts.place,
        has_query: facts.hasQuery,
        query: current.filter.query,
        grade_id: facts.gradeId,
        unit_filter_count: facts.unitFilterCount,
        type_filter_count: facts.typeFilterCount,
        fallback_count: facts.fallback.count,
      });
    }, [track]),

    errorViewed: useCallback(
      () =>
        track('quiz_bank_result_error_view', {
          place: state.current.place,
          grade_id: factsOf(state.current).gradeId,
        }),
      [track],
    ),

    retryClicked: useCallback(
      () =>
        track('quiz_bank_result_retry_click', {
          place: state.current.place,
          grade_id: factsOf(state.current).gradeId,
        }),
      [track],
    ),
  };
};
